import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import MSpinner from "components/Shared/MSpinner.tsx";
import { ExpansionList, ExpansionPanel, SelectField, Button, TextField } from "react-md";
import { addAppraiserExclusion, removeAppraiserExclusion, getAppraiserExcludedCustomers } from "requests/customerAppraiserExclusions";
import { addInspectorExclusion, removeInspectorExclusion, getInspectorExcludedCustomers } from "requests/customerInspectorExclusions";
import { getCustomersByType } from "requests/customer";
import { LENDER, BOTH } from "typeConstants/customerTypes";
import { dateToString } from "components/Shared/Formatters";

const ExclusionaryList = ({ appraiserNumber, inspectorNumber }) => {
  const [exclusionsList, setExclusionsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const mounted = useRef(true);
  const customers = useRef([]);
  const selected = useRef(undefined);
  const comments = useRef(undefined);

  const getExclusionsList = () => {
    if (appraiserNumber) {
      getAppraiserExcludedCustomers(appraiserNumber, getExcludedCallback, mounted);
    } else if (inspectorNumber) {
      getInspectorExcludedCustomers(inspectorNumber, getExcludedCallback, mounted);
    }
  };

  useEffect(() => {
    getExclusionsList();
  }, [appraiserNumber, inspectorNumber]);

  useEffect(() => {
    const customerType = appraiserNumber ? LENDER : BOTH;
    getCustomersByType(customerType, response => {
      customers.current = response;
    }, mounted);
    return () => {
      mounted.current = false;
    };
  }, []);

  const getExcludedCallback = (response) => {
    if (response.length) {
      const excluded = response.map(o => o.customerExclusionLists)
        .reduce((acc, val) => acc.concat(val), [])
        .map(o => {
          return {
            id: o.id,
            displayLabel: o.removedDate 
              ? `INACTIVE - ${o.customers.displayLabel}` 
              : o.customers.displayLabel,
            createdDate: o.createdDate,
            createdByUserEmail: o.createdByUserEmail,
            comments: o.comments,
            removedDate: o.removedDate,
            removedByUserEmail: o.removedByUserEmail
          };
        });
      setExclusionsList(excluded);
    } else {
      setExclusionsList([]);
    }
    setIsLoading(false);
  };

  const blankExclusion = {
    id: undefined,
    displayLabel: "New Exclusion",
    createdDate: new Date()
  };

  const onAddNew = () => {
    setExclusionsList([...exclusionsList, blankExclusion]);
  };

  const onSave = () => {
    if (appraiserNumber) {
      addAppraiserExclusion(selected.current, comments.current, appraiserNumber, getExclusionsList, mounted);
    } else if (inspectorNumber) {
      addInspectorExclusion(selected.current, comments.current, inspectorNumber, getExclusionsList, mounted);
    }
  };

  const onDelete = (id) => {
    if (!id || typeof id === "object") {
      // User deleted an exclusion that wasn't saved
      setExclusionsList(exclusionsList.filter(e => e.id !== undefined));
      return;
    }
    if (appraiserNumber) {
      removeAppraiserExclusion(id, getExclusionsList, mounted);
    } else if (inspectorNumber) {
      removeInspectorExclusion(id, getExclusionsList, mounted);
    }
  };

  if (isLoading) return <MSpinner />;

  return (
    <div className="md-grid">
      <ExpansionList className="md-cell md-cell--12 md-cell--right">
        {exclusionsList && exclusionsList.map((exclusion) => {
          return (
            <ExpansionPanel className="md-cell md-cell--12 md-cell--right"
              key={exclusion.id ?? 0}
              label={exclusion.displayLabel}
              saveProps={{
                flat: true,
                disabled: exclusion.id ? true : false
              }}
              cancelProps={{
                flat: true
              }}
              onSave={onSave}
              onCancel={exclusion.id ? () => { } : onDelete}
              contentClassName="md-grid"
            >
              {exclusion.id &&
                <div className="full-width">
                  <p>Added {dateToString(exclusion.createdDate)} by {exclusion.createdByUserEmail}</p>
                  <br />
                  {exclusion.removedDate &&
                    <><p>Removed {dateToString(exclusion.removedDate)} by {exclusion.removedByUserEmail}</p>
                    <br /></>
                  }
                </div>
              }
              {!exclusion.id &&
                <>
                  <SelectField
                    id="exclusion-customer-select"
                    label="Customer"
                    itemLabel="name"
                    itemValue="id"
                    menuItems={customers.current.map(x => ({ name: x.displayLabel, id: x.number }))}
                    className="md-cell md-cell--12 md-cell--right"
                    onChange={(value) => {
                      selected.current = value;
                    }}
                    value={selected.current}
                  />
                  <TextField
                    id="exclusion-comments-text-field"
                    label="Comments"
                    onChange={(value) => {
                      comments.current = value;
                    }}
                    value={exclusion.comments}
                  />
                </>}
              {exclusion.id &&
                <>
                  {exclusion.comments &&
                    <div className="full-width">
                      <p>{exclusion.comments}</p>
                    </div>}
                  {!exclusion.removedDate && <div className="md-cell md-cell--12 offset-md--4">
                    <i className="md-icon material-icons" onClick={() => onDelete(exclusion.id)}>delete</i>
                  </div>}
                </>}
            </ExpansionPanel>);
        })}
      </ExpansionList>
      <Button className="md-cell md-cell--12 md-cell--right"
        flat
        secondary
        iconChildren="add"
        onClick={onAddNew}>
        Add Exclusion
        </Button>
    </div>
  );
};

ExclusionaryList.propTypes = {
  appraiserNumber: PropTypes.number,  // If appraiser number is passed, this will call Customer Appraiser Exclusions endpoints
  inspectorNumber: PropTypes.number,  // If inspector number is passed, this will call Customer Inspector Exclusions endpoints
};

export default ExclusionaryList;